import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { Btn, Eyebrow, DisplayHeading, SectionHeader } from "@/components/marketing/primitives";
import { HomeResults } from "@/components/marketing/HomeResults";
import { HomeScanCard } from "@/components/marketing/HomeScanCard";
import { HomeChannels } from "@/components/marketing/HomeChannels";
import { HOME_RESULTS } from "@/content/homeRefresh";
import { Marquee } from "@/components/marketing/Marquee";
import { TrackingCensus } from "@/components/marketing/TrackingCensus";
import { IndustryList } from "@/components/marketing/IndustryList";
import { ProcessSteps } from "@/components/marketing/ProcessSteps";
import { FAQ } from "@/components/marketing/FAQ";
import { ContactCTA } from "@/components/marketing/ContactCTA";
import { INDUSTRY_NAV } from "@/content/industryNav";
import {
  CLIENT_NAMES,
  PROCESS,
  FAQS,
  TESTIMONIALS,
} from "@/content/site";
import { OG_IMAGE, ORG_ID, SITE, WEBSITE_ID, abs } from "@/content/siteConfig";


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Home Services Marketing Agency | Ads, SEO & AI | Berk Hospitality" },
      {
        name: "description",
        content:
          "Google and Meta ads, SEO and AI search, and AI that answers every lead, for home services companies. Scottsdale-based Google Partner. Free tracking scan.",
      },
      {
        property: "og:title",
        content: "Home Services Marketing Agency | Ads, SEO & AI | Berk Hospitality",
      },
      {
        name: "twitter:title",
        content: "Home Services Marketing Agency | Ads, SEO & AI | Berk Hospitality",
      },
      {
        property: "og:description",
        content:
          "Google and Meta ads, SEO and AI search, and AI that answers every lead, for home services companies. Scottsdale-based Google Partner. Free tracking scan.",
      },
      {
        name: "twitter:description",
        content:
          "Google and Meta ads, SEO and AI search, and AI that answers every lead, for home services companies. Scottsdale-based Google Partner. Free tracking scan.",
      },
      { property: "og:url", content: abs("/") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: abs("/") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": WEBSITE_ID,
          name: "Berk Hospitality Group",
          url: SITE.origin,
          publisher: { "@id": ORG_ID },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Index,
});


function Index() {
  return (
    <SiteShell>
      <header className="home-refresh-hero">
        <div className="wrap home-refresh-hero-grid">
          <div>
            <Eyebrow>Scottsdale, AZ · Est. 2021 · EN / ES</Eyebrow>
            <h1>Get found, get the call, <span className="display-italic">book the job.</span></h1>
            <p className="home-refresh-lede">The marketing team for home services companies across the US. Google and Meta ads, SEO and AI search, and automation that answers every lead.</p>
            <div className="home-refresh-buttons">
            <Btn to="/contact">
              Book a 30-minute call →
            </Btn>
            <Btn to="/services" variant="ghost">
              View services
            </Btn>
            </div>
            <div className="home-refresh-badges"><span><i aria-hidden="true" />Certified Google Partner</span><span><i aria-hidden="true" />Meta Business Partner</span></div>
          </div>
          <HomeScanCard />
        </div>
        <div className="wrap"><HomeResults /></div>
      </header>

      <div className="homepage-content">
        <Marquee items={CLIENT_NAMES} label="Selected clients" />

        <section className="bhg-section home-services">
          <div className="wrap">
            <SectionHeader num="Services" title={<>Four channels. One <span className="display-italic">thesis.</span></>}>
              We don&apos;t sell content packages or hourly retainers. We run paid media, and we treat
              your account as an asset that compounds, not a campaign that resets every quarter.
              That is the whole of <Link to="/about" className="home-context-link">how our Scottsdale team works</Link>,
              with operators across the Phoenix metro and accounts nationwide.
            </SectionHeader>
            <HomeChannels />
          </div>
        </section>

        <section className="bhg-section home-research">
          <div className="wrap home-split">
            <SectionHeader num="Research" title={<>We checked 9,560 home services businesses. Most can&apos;t <span className="display-italic">measure what they&apos;re buying.</span></>} />
            <div>
              <p className="home-prose mt-6">
                Before we pitch anyone, we check whether their advertising can be measured at all.
                So we ran the same check across the market: <Link to="/research/conversion-tracking-report" className="home-context-link">9,560 home services websites as of October 2026</Link>,
                each loaded in a real browser. That is the problem we fix first on every account,
                because an optimization made against <Link to="/blog/$slug" params={{ slug: "why-does-meta-report-more-purchases-than-my-store" }} className="home-context-link">broken measurement</Link> makes the account worse.
              </p>
              <div className="mt-8"><Btn to="/research/conversion-tracking-report" variant="ghost">Read the full research →</Btn></div>
            </div>
            <div className="home-research-data">
              <div className="home-research-number tnum text-champagne" data-countup="">27.4%</div>
              <p className="home-prose mt-6"><strong className="font-medium text-bleu">More than 1 in 4</strong> of the 1,807 businesses we confirmed running ads had no conversion tracking of any kind. Not misconfigured: absent.</p>
              <div className="home-percent-census mt-8"><TrackingCensus total={100} hollow={27} cols={20} mobileCols={20} /></div>
              <p className="mono mt-4 text-[11px] text-bleu-faint">Each dot is 1% · hollow = no tracking</p>
            </div>
          </div>
        </section>

        <section className="bhg-section home-outcomes">
          <div className="wrap home-split">
            <div>
              <SectionHeader num="Outcomes" title={<>What that&apos;s actually <span className="display-italic">produced.</span></>} />
              <p className="home-prose mt-6">We report two things separately, because most of the industry blurs them: what the advertising produced, and what actually closed. Our longest-running Google Ads account has been managed continuously since August 2015.</p>
              <div className="mt-8"><Btn to="/case-studies" variant="ghost">See the numbers behind these →</Btn></div>
            </div>
            <div>
              <div className="home-outcome-grid grid grid-cols-2 gap-x-7 gap-y-8">
                {[
                  ["$90,000", "from one lead, on a landscaping campaign the client nearly switched off for looking expensive per lead."],
                  ["$50,000", "of roofing work booked from two deals in a single month."],
                  ["$33,435", "booked by a commercial door company across two months, on an account whose cost per lead is unremarkable."],
                  [`$${HOME_RESULTS.revenue}M+`, "in booked revenue closed by clients from campaigns we run. Closed work, not leads."],
                ].map(([number, body]) => <div key={number} className="border-t border-line pt-5">
                  <div className="home-outcome-number tnum" data-countup="">{number}</div><p className="mt-3 text-[15px] leading-[1.55] text-bleu-dim">{body}</p>
                </div>)}
              </div>
            </div>
            <div className="home-testimonials">
              <Eyebrow>In their words</Eyebrow>
              <div className="home-quote-row">
                {TESTIMONIALS.map(t => <figure key={t.name} className="home-quote-card">
                  <blockquote>“{t.body}”</blockquote>
                  <figcaption className="mono"><strong>{t.name}</strong> · {t.role}, {t.company}</figcaption>
                </figure>)}
              </div>
            </div>
          </div>
        </section>

        <section className="bhg-section home-industries">
          <div className="wrap home-split">
            <div>
              <Eyebrow>Industries</Eyebrow>
              <DisplayHeading as="h2">Industries we run paid media <span className="display-italic">for.</span></DisplayHeading>
              <p className="home-prose mt-6">From local service operators to consumer brands, we build paid-media systems engineered for measurable growth: the same operating model, applied across categories.</p>
              <blockquote className="home-industry-quote mt-8">Every new account looks like one we&apos;ve already optimized.</blockquote>
              <div className="mt-8"><Btn to="/industries" variant="ghost">See all industries →</Btn></div>
            </div>
            <IndustryList compact items={INDUSTRY_NAV.map(i => ({ idx: i.idx, name: i.name, to: i.to }))} />
          </div>
        </section>

        <section className="bhg-section home-faq">
          <div className="wrap home-split">
            <div className="home-sticky-heading">
              <SectionHeader num="FAQ" title={<>Common <span className="display-italic">questions.</span></>} />
              <p className="home-prose mt-6">Straight answers to what operators ask before they hire us. The rest is on our company facts page.</p>
              <div className="mt-8"><Btn to="/ai" variant="ghost">Company facts →</Btn></div>
            </div>
            <FAQ items={FAQS} native initiallyOpen={false} />
          </div>
        </section>

        <ContactCTA num="Get in touch" homepage>
          <div className="home-process">
            <p className="mono mb-8 text-[11px] text-bleu-faint">HOW WE WORK · FOUR PHASES, WITH THE MEASUREMENT IN PLACE BEFORE SPEND SCALES</p>
            <ProcessSteps steps={PROCESS} />
          </div>
        </ContactCTA>
      </div>
    </SiteShell>
  );
}
